import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useSiteContent } from '../context/SiteContentContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'all', label: 'All' },
  { id: 'audio-video', label: 'A/V Audio Video' },
  { id: 'theater-rooms', label: 'Theater Rooms' },
  { id: 'specialty-items', label: 'Specialty Items' },
  { id: 'pre-wire', label: 'Pre-Wire / Construction' },
  { id: 'customizations', label: 'Customizations' },
];

export default function GalleryPage() {
  const { c } = useSiteContent();
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    setItems([]);
    const cat = activeCategory === 'all' ? '' : activeCategory;
    axios.get(`${API}/gallery${cat ? `?category=${cat}` : ''}`)
      .then(r => setItems(r.data))
      .catch(() => {});
  }, [activeCategory]);

  return (
    <div className="min-h-screen pt-24" data-testid="gallery-page">
      {/* Hero */}
      <section className="py-20 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-manrope font-semibold mb-4">Our Portfolio</p>
            <h1 className="font-playfair text-4xl lg:text-5xl text-white mb-4">{c('gallery.title', 'Our Gallery')}</h1>
            <p className="text-white/40 font-manrope text-lg max-w-2xl leading-relaxed">
              {c('gallery.subtitle', 'Discover our exceptional designs, innovative layouts, premium materials, and expertly crafted setups. Find inspiration for your dream space.')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-white/5 sticky top-16 lg:top-20 z-40 backdrop-blur-xl bg-[#0f0f10]/90">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`
                  px-4 py-2 rounded-sm font-manrope text-xs uppercase tracking-widest transition-all duration-300
                  ${activeCategory === cat.id
                    ? 'bg-[#D4AF37] text-black font-bold'
                    : 'text-white/40 border border-white/10 hover:border-[#D4AF37]/30 hover:text-white/70'
                  }
                `}
                data-testid={`filter-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-grid">
            <AnimatePresence mode="popLayout">
              {items.map((item, i) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  className="group cursor-pointer"
                  onClick={() => setSelectedItem(item)}
                  data-testid={`gallery-item-${item.id}`}
                >
                  <div className="aspect-[4/3] rounded-sm overflow-hidden bg-[#1a1a1a] relative">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="font-manrope font-semibold text-white text-sm">{item.title}</h3>
                        <p className="text-white/50 text-xs font-manrope mt-1">{item.category.replace('-', ' ')}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {items.length === 0 && (
            <div className="text-center py-20">
              <p className="text-white/30 font-manrope">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 lg:p-8"
            onClick={() => setSelectedItem(null)}
            data-testid="gallery-lightbox"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="max-w-4xl w-full"
              onClick={e => e.stopPropagation()}
            >
              <img
                src={selectedItem.image_url}
                alt={selectedItem.title}
                className="w-full rounded-sm"
              />
              <div className="mt-4">
                <h3 className="font-playfair text-xl text-white">{selectedItem.title}</h3>
                <p className="text-white/40 font-manrope text-sm mt-1">{selectedItem.description}</p>
              </div>
              <button
                onClick={() => setSelectedItem(null)}
                className="absolute top-6 right-6 text-white/50 hover:text-white text-2xl font-manrope"
                data-testid="lightbox-close"
              >
                &times;
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
